import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'business' | 'dark' | 'cyberpunk';

interface ThemeContextType {
    theme: Theme;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    // Default to 'business' as per user request for "default - clean white grey and blue"
    // Or 'dark' if they prefer dark mode. Let's make 'business' the light mode and 'dark' the current one.
    // Checking localStorage first.
    const [theme, setTheme] = useState<Theme>(() => {
        const saved = localStorage.getItem('wranglr-theme');
        return (saved as Theme) || 'business'; // Default to 'business' (light)
    });

    useEffect(() => {
        const root = window.document.documentElement;
        // Remove old classes if we used them
        root.classList.remove('theme-business', 'theme-dark', 'theme-cyberpunk');

        // set data-theme attribute
        root.setAttribute('data-theme', theme);

        localStorage.setItem('wranglr-theme', theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
